import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Divider, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CustomSwitch from "../../../components/CustomSwitch";
import CustomTextField from "../../../components/CustomTextField";
import Select from "../../../components/Select/Select";
import { AppColors } from "../../../util/AppColors";
import {
  getWhatsAppEmbeddedSignupMissingVars,
  isWhatsAppEmbeddedSignupConfigured,
  launchWhatsAppEmbeddedSignup,
} from "../../../util/whatsappEmbeddedSignup";
import "../BranchManagement/BranchManagement.css";
import "./GlobalSettings.css";

const toDayjsTime = (t) => {
  if (!t) return null;
  if (dayjs.isDayjs(t)) return t;
  if (typeof t === "string" && /^\d{1,2}:\d{2}/.test(t)) {
    const [hh, mm] = t.split(":");
    return dayjs().hour(Number(hh)).minute(Number(mm)).second(0).millisecond(0);
  }
  return dayjs(t);
};

const fromDayjsTime = (t) => {
  if (!t) return "";
  return dayjs(t).format("HH:mm");
};

const emailProvidersData = [
  {
    key: "google",
    name: "Google Workspace",
    icon: "G",
    mockEmail: "paws@vetinstant.com",
    steps: [
      {
        title: "Sign in with Google Workspace",
        desc: "Use your clinic's Google Workspace admin account.",
        cta: "Continue with Google",
      },
      {
        title: "Grant mail-send permission",
        desc: "Approve the OAuth consent screen so PAWS can send mail as your clinic's address.",
        cta: "Grant access",
      },
    ],
  },
  {
    key: "microsoft",
    name: "Microsoft 365",
    icon: "M",
    mockEmail: "paws@vetinstant.com",
    steps: [
      {
        title: "Sign in with Microsoft 365",
        desc: "Use your organization's Microsoft work account.",
        cta: "Continue with Microsoft",
      },
      {
        title: "Approve Mail.Send permission",
        desc: "Your Azure AD administrator may need to approve this the first time.",
        cta: "Approve permissions",
      },
    ],
  },
  {
    key: "zoho",
    name: "Zoho Mail",
    icon: "Z",
    mockEmail: "paws@vetinstant.com",
    steps: [
      {
        title: "Sign in to Zoho Mail",
        desc: "Use your clinic's Zoho Mail account.",
        cta: "Continue with Zoho",
      },
      {
        title: "Enable Two-Factor Authentication",
        desc: "Zoho requires 2FA to be turned on before an app-specific password can be generated.",
        cta: "I've enabled 2FA",
      },
      {
        title: "Generate an app password",
        desc: "Go to Zoho Mail → Settings → Security → App Passwords.",
        cta: "I've generated it",
      },
      {
        title: "Paste the app password",
        desc: "Paste the app-specific password Zoho generated.",
        cta: "Connect",
        input: true,
      },
    ],
  },
];

const countrySmsFields = {
  India: {
    note: "Requires DLT sender-ID & template registration (TRAI).",
    fields: [
      { key: "entityId", label: "DLT Entity ID", width: 6, showLabel: false },
      {
        key: "templateId",
        label: "DLT Template ID",
        width: 6,
        showLabel: false,
      },
      {
        key: "senderId",
        label: "Registered sender ID (6 chars)",
        width: 6,
        showLabel: false,
      },
    ],
  },
  Singapore: {
    note: "Sender ID registration via IMDA SSIR recommended.",
    fields: [
      {
        key: "senderId",
        label: "Registered sender ID",
        width: 6,
        showLabel: true,
      },
    ],
  },
  Malaysia: {
    note: "Local sender ID registration required by most carriers.",
    fields: [
      {
        key: "senderId",
        label: "Registered sender ID",
        width: 6,
        showLabel: true,
      },
    ],
  },
  UAE: {
    note: "TRA content pre-approval required for marketing sends.",
    fields: [
      {
        key: "senderId",
        label: "Approved sender ID",
        width: 6,
        showLabel: true,
      },
    ],
  },
  Australia: {
    note: "Standard alphanumeric sender ID, no pre-registration.",
    fields: [
      { key: "senderId", label: "Sender ID", width: 6, showLabel: true },
    ],
  },
  NewZealand: {
    note: "Standard alphanumeric sender ID, no pre-registration.",
    fields: [
      { key: "senderId", label: "Sender ID", width: 6, showLabel: true },
    ],
  },
};

const countryOptions = [
  { name: "India", value: "India" },
  { name: "Singapore", value: "Singapore" },
  { name: "Malaysia", value: "Malaysia" },
  { name: "United Arab Emirates", value: "UAE" },
  { name: "Australia", value: "Australia" },
  { name: "New Zealand", value: "NewZealand" },
];

const notificationPurposes = [
  "Payment Reminder",
  "Common Reminder",
  "Appointment Reminder",
  "EMR Share",
  "Prescription",
  "Invoice & Receipt",
];

const whatsappSignupSteps = [
  "Confirm your business details with Meta",
  "Choose or verify a phone number for WhatsApp Business",
  "Grant permission for messaging via our platform",
];

const sectionHeaderStyle = {
  color: "#6B7280",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  fontSize: 11,
};

const CommunicationSettings = () => {
  const [connectedEmails, setConnectedEmails] = useState({});
  const [activeConnectKey, setActiveConnectKey] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [appPasswordInput, setAppPasswordInput] = useState("");
  const [testEmailSent, setTestEmailSent] = useState(false);

  // null = not started, "signup" = Meta flow, true = connected
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [whatsappConnecting, setWhatsappConnecting] = useState(false);
  const [whatsappConnection, setWhatsappConnection] = useState(null);
  const [whatsappError, setWhatsappError] = useState("");

  const [selectedCountry, setSelectedCountry] = useState("India");
  const [smsFieldValues, setSmsFieldValues] = useState({});

  const [routingMatrix, setRoutingMatrix] = useState({
    "Payment Reminder": { Email: true, SMS: true, WhatsApp: true },
    "Common Reminder": { Email: true, SMS: false, WhatsApp: false },
    "Appointment Reminder": { Email: true, SMS: false, WhatsApp: true },
    "EMR Share": { Email: false, SMS: false, WhatsApp: true },
    Prescription: { Email: false, SMS: false, WhatsApp: true },
    "Invoice & Receipt": { Email: true, SMS: false, WhatsApp: true },
  });

  const [fallbackAuto, setFallbackAuto] = useState(true);
  const [quietHoursStart, setQuietHoursStart] = useState("21:00");
  const [quietHoursEnd, setQuietHoursEnd] = useState("08:00");

  const toggleRoutingCell = (purpose, channel) => {
    setRoutingMatrix((prev) => ({
      ...prev,
      [purpose]: {
        ...prev[purpose],
        [channel]: !prev[purpose][channel],
      },
    }));
  };

  const handleLaunchWhatsAppEmbeddedSignup = useCallback(async () => {
    setWhatsappError("");
    setWhatsappStatus("signup");

    if (!isWhatsAppEmbeddedSignupConfigured()) {
      const missing = getWhatsAppEmbeddedSignupMissingVars();
      setWhatsappError(
        `Missing in .env: ${missing.join(", ")}. ` +
          (missing.includes("REACT_APP_WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID")
            ? "Get Configuration ID from Meta → Facebook Login for Business → Configurations (Business ID is not the Config ID). Then restart npm start."
            : "Then restart npm start.")
      );
      return;
    }

    setWhatsappConnecting(true);
    try {
      const result = await launchWhatsAppEmbeddedSignup();
      // Pass result.code to your backend to exchange for a business token,
      // subscribe webhooks, and register the phone number.
      setWhatsappConnection({
        wabaId: result.wabaId,
        phoneNumberId: result.phoneNumberId,
        code: result.code,
      });
      setWhatsappStatus(true);
    } catch (err) {
      setWhatsappError(
        err?.message || "Could not complete WhatsApp Embedded Signup."
      );
      setWhatsappStatus(null);
    } finally {
      setWhatsappConnecting(false);
    }
  }, []);

  const handleStartConnect = (providerKey) => {
    setActiveConnectKey(providerKey);
    setCurrentStepIndex(0);
    setAppPasswordInput("");
  };

  const handleCancelConnect = () => {
    setActiveConnectKey(null);
    setCurrentStepIndex(0);
    setAppPasswordInput("");
  };

  const handleConnectStepNext = () => {
    const provider = emailProvidersData.find((p) => p.key === activeConnectKey);
    if (!provider) return;
    if (currentStepIndex < provider.steps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      setConnectedEmails((prev) => ({ ...prev, [activeConnectKey]: true }));
      handleCancelConnect();
    }
  };

  const handleSendTestEmail = () => {
    setTestEmailSent(true);
    setTimeout(() => setTestEmailSent(false), 4000);
  };

  const handleSave = () => {
    // Persist communication settings when API is wired.
  };

  const smsConfig = countrySmsFields[selectedCountry];

  return (
    <div className="consultation-white-con scroll-80vh p20">
      <div className="flex-row-between-align-center mb20 pb10">
        <div>
          <Typography className="text-bold fs20 black">
            Communication
          </Typography>
          <Typography className="txt-regular fs13 card-gray-color mt4">
            Connect channels and decide what gets sent where — configured per
            branch.
          </Typography>
        </div>
        <div>
          <div style={{ width: 140 }}>
            <CustomButton text="Save Changes" onClick={handleSave} smallBtn />
          </div>
        </div>
      </div>
      <Divider />

      {/* 1. Email (SMTP) */}
      <div className="back-white br12 border p20 mt20 mb20">
        <Typography className="text-bold fs15 black">Email (SMTP)</Typography>
        <Typography className="txt-regular fs12 card-gray-color mt2 mb15">
          Each provider follows its own real connection flow — not a single
          generic email + password box.
        </Typography>

        <div className="mb15" style={{ display: "grid", gap: 10 }}>
          {emailProvidersData.map((provider) => {
            const isConn = !!connectedEmails[provider.key];
            const isExpanding = activeConnectKey === provider.key;
            const step = provider.steps[currentStepIndex];
            const stepCount = provider.steps.length;

            return (
              <div
                key={provider.key}
                style={{
                  border: isConn ? "1px solid #86EFAC" : "1px solid #E5E7EB",
                  borderRadius: 10,
                  padding: "12px 14px",
                  background: isConn ? "#F0FDF4" : "#fff",
                }}
              >
                <div
                  className="flex-row-between-align-center"
                  style={{ gap: 12 }}
                >
                  <div
                    className="flex-row-ali-cen"
                    style={{ gap: 12, flex: 1, minWidth: 0 }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: "1px solid #E5E7EB",
                        background: "#F3F4F6",
                        color: "#4B5563",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {provider.icon}
                    </div>
                    <div>
                      <Typography className="text-bold fs14 black">
                        {provider.name}
                      </Typography>
                      <Typography
                        className="fs12"
                        style={{
                          color: isConn ? "#16A34A" : "#9CA3AF",
                          fontWeight: isConn ? 600 : 400,
                        }}
                      >
                        {isConn ? provider.mockEmail : "Not connected"}
                      </Typography>
                    </div>
                  </div>

                  {isConn ? (
                    <div style={{ flexShrink: 0 }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          border: "1px solid #86EFAC",
                          background: "#fff",
                          color: "#16A34A",
                          borderRadius: 8,
                          padding: "6px 12px",
                          fontSize: 12,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <CheckCircleOutlinedIcon sx={{ fontSize: 14 }} />{" "}
                        Connected
                      </span>
                    </div>
                  ) : isExpanding ? (
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        minWidth: 100,
                        width: 100,
                        flexShrink: 0,
                      }}
                    >
                      <CustomButton
                        text="Cancel"
                        smallBtn
                        onClick={handleCancelConnect}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        minWidth: 100,
                        width: 100,
                        flexShrink: 0,
                      }}
                    >
                      <CustomButton
                        text="Connect"
                        smallBtn
                        onClick={() => handleStartConnect(provider.key)}
                      />
                    </div>
                  )}
                </div>

                {isExpanding && step ? (
                  <div
                    style={{
                      marginTop: 12,
                      borderTop: "1px solid #E5E7EB",
                      paddingTop: 14,
                    }}
                  >
                    <div className="flex-row-ali-cen mb10" style={{ gap: 6 }}>
                      {provider.steps.map((_, idx) => (
                        <div
                          key={`${provider.key}-seg-${idx}`}
                          style={{
                            flex: 1,
                            height: 4,
                            borderRadius: 999,
                            background:
                              idx <= currentStepIndex
                                ? AppColors.appPrimary || "#0054A6"
                                : "#DBEAFE",
                          }}
                        />
                      ))}
                    </div>
                    <Typography className="fs12 card-gray-color mb8">
                      Step {currentStepIndex + 1} of {stepCount}
                    </Typography>
                    <Typography className="text-bold fs14 black mb4">
                      {step.title}
                    </Typography>
                    <Typography className="txt-regular fs12 card-gray-color mb12">
                      {step.desc}
                    </Typography>
                    {step.input ? (
                      <div className="mb12" style={{ maxWidth: 360 }}>
                        <CustomTextField
                          placeholder="App Password"
                          type="password"
                          value={appPasswordInput}
                          handleChange={(e) =>
                            setAppPasswordInput(e.target.value)
                          }
                          fullWidth
                        />
                      </div>
                    ) : null}
                    <div
                      style={{
                        width: "fit-content",
                        minWidth: 180,
                        maxWidth: "100%",
                      }}
                    >
                      <CustomButton
                        text={step.cta}
                        smallBtn
                        onClick={handleConnectStepNext}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="flex-row-ali-cen mt10" style={{ gap: 10 }}>
          <div
            style={{
              whiteSpace: "nowrap",
              width: "fit-content",
              minWidth: 150,
              flexShrink: 0,
            }}
          >
            <CustomButton
              text="Send test email"
              whiteBtn
              smallBtn
              onClick={handleSendTestEmail}
            />
          </div>
          {testEmailSent ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                border: "1px solid #86EFAC",
                background: "#F0FDF4",
                color: "#16A34A",
                borderRadius: 999,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              ✓ Test email sent
            </span>
          ) : null}
        </div>
      </div>

      {/* 2. WhatsApp Business */}
      <div className="back-white br12 border p20 mb20">
        <div className="flex-row-between-align-center mb10">
          <div>
            <Typography className="text-bold fs15 black">
              WhatsApp Business
            </Typography>
            <Typography className="txt-regular fs12 card-gray-color mt2">
              Connected via embedded signup through our WhatsApp Business
              Solution Provider.
            </Typography>
          </div>
          {whatsappStatus === true ? (
            <div style={{ flexShrink: 0 }}>
              <span
                className="app-status-badge approved flex-row-ali-cen fs12"
                style={{ gap: 4, whiteSpace: "nowrap" }}
              >
                <CheckCircleOutlinedIcon sx={{ fontSize: 14 }} /> Connected
              </span>
            </div>
          ) : null}
        </div>

        {whatsappError ? (
          <Typography
            className="fs12 mb12"
            style={{ color: "#B91C1C", background: "#FEF2F2", padding: 10, borderRadius: 8 }}
          >
            {whatsappError}
          </Typography>
        ) : null}

        {whatsappStatus === true ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <div className="p12 br8 border" style={{ background: "#F9FAFB" }}>
                <Typography style={sectionHeaderStyle}>Phone Number ID</Typography>
                <Typography className="text-bold fs14 black mt3">
                  {whatsappConnection?.phoneNumberId || "Pending backend register"}
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} sm={4}>
              <div className="p12 br8 border" style={{ background: "#F9FAFB" }}>
                <Typography style={sectionHeaderStyle}>WABA ID</Typography>
                <Typography className="text-bold fs14 black mt3">
                  {whatsappConnection?.wabaId || "—"}
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} sm={4}>
              <div className="p12 br8 border" style={{ background: "#F9FAFB" }}>
                <Typography style={sectionHeaderStyle}>Auth code</Typography>
                <Typography className="text-bold fs14 black mt3">
                  {whatsappConnection?.code
                    ? `${String(whatsappConnection.code).slice(0, 12)}…`
                    : "—"}
                </Typography>
              </div>
            </Grid>
          </Grid>
        ) : whatsappStatus === "signup" ? (
          <div
            style={{
              background: "#F3F6FA",
              border: "1px solid #E5E7EB",
              borderRadius: 12,
              padding: 16,
            }}
          >
            <Typography className="text-bold fs13 black mb12">
              Embedded signup — Meta will open a guided window to:
            </Typography>
            <div className="mb14" style={{ display: "grid", gap: 10 }}>
              {whatsappSignupSteps.map((text, idx) => (
                <div
                  key={text}
                  className="flex-row-ali-cen"
                  style={{ gap: 10 }}
                >
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "#0054A6",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {idx + 1}
                  </span>
                  <Typography className="fs13" style={{ color: "#4B5563" }}>
                    {text}
                  </Typography>
                </div>
              ))}
            </div>
            <div
              style={{ width: "fit-content", minWidth: 240 }}
              className="mt10"
            >
              <CustomButton
                text={
                  whatsappConnecting
                    ? "Opening Meta…"
                    : "Continue in Meta window →"
                }
                smallBtn
                disabled={whatsappConnecting}
                onClick={handleLaunchWhatsAppEmbeddedSignup}
              />
            </div>
          </div>
        ) : (
          <div style={{ width: "fit-content" }}>
            <button
              type="button"
              onClick={handleLaunchWhatsAppEmbeddedSignup}
              disabled={whatsappConnecting}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "#22C55E",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 16px",
                fontSize: 13,
                fontWeight: 700,
                cursor: whatsappConnecting ? "wait" : "pointer",
                whiteSpace: "nowrap",
                opacity: whatsappConnecting ? 0.75 : 1,
              }}
            >
              <WhatsAppIcon sx={{ fontSize: 18 }} />
              {whatsappConnecting
                ? "Connecting…"
                : "Connect WhatsApp Business"}
            </button>
          </div>
        )}

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            borderRadius: 999,
            padding: "6px 12px",
            fontSize: 12,
            fontWeight: 500,
            marginTop: 14,
            background: whatsappStatus === true ? "#EFF6FF" : "#F3F4F6",
            color: whatsappStatus === true ? "#1D4ED8" : "#6B7280",
          }}
        >
          Template library is managed centrally by your organization
        </div>
      </div>

      {/* 3. SMS */}
      <div className="back-white br12 border p20 mb20">
        <div className="flex-row-between-align-center mb10">
          <div>
            <Typography className="text-bold fs15 black">SMS</Typography>
            <Typography className="txt-regular fs12 card-gray-color mt2">
              Shown as a provision for now — full carrier integration is scoped
              for a later implementation pass.
            </Typography>
          </div>
          <span
            className="app-priority-badge medium flex-row-ali-cen"
            style={{ gap: 4, flexShrink: 0, whiteSpace: "nowrap" }}
          >
            <WarningAmberRoundedIcon sx={{ fontSize: 14 }} /> Provisional
          </span>
        </div>

        <div style={{ maxWidth: 280 }} className="mb12">
          <Typography className="txt-semi-bold fs12 card-gray-color mb5">
            Branch country
          </Typography>
          <Select
            list={countryOptions}
            value={selectedCountry}
            handleChange={(e) => {
              setSelectedCountry(e?.target?.value);
              setSmsFieldValues({});
            }}
            select
          />
        </div>

        <div
          className="p10 br8 amber-bg border-amber flex-row-ali-cen mv10"
          style={{ gap: 8 }}
        >
          <InfoOutlinedIcon sx={{ color: "#b45309", fontSize: 18 }} />
          <Typography className="fs12 font-semibold amber-color">
            {smsConfig?.note}
          </Typography>
        </div>

        <Grid container spacing={2}>
          {(smsConfig?.fields ?? []).map((field) => (
            <Grid item xs={12} sm={field.width} key={field.key}>
              {field.showLabel ? (
                <Typography className="txt-semi-bold fs12 card-gray-color mb5">
                  {field.label}
                </Typography>
              ) : null}
              <CustomTextField
                placeholder={field.label}
                value={smsFieldValues?.[field.key] ?? ""}
                handleChange={(e) =>
                  setSmsFieldValues((prev) => ({
                    ...prev,
                    [field.key]: e?.target?.value,
                  }))
                }
                fullWidth
              />
            </Grid>
          ))}
        </Grid>
      </div>

      {/* 4. Notification routing */}
      <div className="back-white br12 border p20">
        <Typography className="text-bold fs15 black">
          Notification routing
        </Typography>
        <Typography className="txt-regular fs12 card-gray-color mt2 mb15">
          This branch can tune routing independently — a smaller branch might
          rely on WhatsApp alone where a bigger one uses all three.
        </Typography>

        <div className="mb15">
          <div
            className="flex-row-ali-cen py10"
            style={{ gap: 12, padding: "0 4px" }}
          >
            <div style={{ flex: 1, ...sectionHeaderStyle }}>Purpose</div>
            <div
              style={{ width: 90, textAlign: "center", ...sectionHeaderStyle }}
            >
              Email
            </div>
            <div
              style={{ width: 90, textAlign: "center", ...sectionHeaderStyle }}
            >
              SMS
            </div>
            <div
              style={{ width: 90, textAlign: "center", ...sectionHeaderStyle }}
            >
              WhatsApp
            </div>
          </div>
          <Divider />
          {notificationPurposes.map((pName, index) => (
            <div key={pName}>
              <div
                className="flex-row-ali-cen"
                style={{
                  gap: 12,
                  padding: "10px 4px",
                  marginTop: 3,
                  marginBottom: 3,
                }}
              >
                <div
                  className="black"
                  style={{ flex: 1, fontWeight: 600, fontSize: 13 }}
                >
                  {pName}
                </div>
                {["Email", "SMS", "WhatsApp"].map((ch) => (
                  <div
                    key={ch}
                    style={{
                      width: 90,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <CustomCheckbox
                      checked={!!routingMatrix?.[pName]?.[ch]}
                      onChange={() => toggleRoutingCell(pName, ch)}
                    />
                  </div>
                ))}
              </div>
              {index < notificationPurposes.length - 1 ? <Divider /> : null}
            </div>
          ))}
        </div>

        <Divider />
        <div
          className="flex-row-between-align-center"
          style={{ marginTop: 3, marginBottom: 3, padding: "12px 0" }}
        >
          <div>
            <Typography className="text-bold fs13 black">
              Fall back automatically
            </Typography>
            <Typography className="txt-regular fs12 card-gray-color mt2">
              If WhatsApp delivery fails, retry via SMS then Email.
            </Typography>
          </div>
          <div style={{ flexShrink: 0 }}>
            <CustomSwitch
              value={!!fallbackAuto}
              onChange={() => setFallbackAuto((prev) => !prev)}
              grayToBlue
            />
          </div>
        </div>
        <Divider />

        <div
          className="flex-row-between-align-center"
          style={{ marginTop: 3, marginBottom: 3, padding: "12px 0" }}
        >
          <div style={{ maxWidth: "55%" }}>
            <Typography className="text-bold fs13 black">
              Quiet hours
            </Typography>
            <Typography className="txt-regular fs12 card-gray-color mt2">
              Non-urgent reminders won&apos;t send outside this window
              (branch-local time).
            </Typography>
          </div>
          <div className="flex-row-ali-cen" style={{ gap: 8, flexShrink: 0 }}>
            <div style={{ width: 130 }}>
              <CustomTextField
                name="quietHoursStart"
                value={toDayjsTime(quietHoursStart)}
                handleChange={(e) =>
                  setQuietHoursStart(fromDayjsTime(e?.target?.value))
                }
                fullWidth
                mobileTime
                is12HourFomat={false}
              />
            </div>
            <Typography className="fs12 card-gray-color">to</Typography>
            <div style={{ width: 130 }}>
              <CustomTextField
                name="quietHoursEnd"
                value={toDayjsTime(quietHoursEnd)}
                handleChange={(e) =>
                  setQuietHoursEnd(fromDayjsTime(e?.target?.value))
                }
                fullWidth
                mobileTime
                is12HourFomat={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunicationSettings;
